/** Investment proposals — saved plans, opening one, and running a new analysis. */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import { useDispatch, useStore } from 'react-redux';
import { INVESTMENT_RECOMMENDATION, LIST_INVESTMENT_PROPOSALS, RUN_INVESTMENT_ANALYSIS } from '../queries';
import { asClient } from '../clientScope';
import {
  analysisJobCleared, analysisJobFinished, analysisJobStarted, selectAnalysisJob,
} from '../../state/analysisJobsSlice';
import { EMPTY } from './_shared';
import { nameFor, nameIndex } from '../../lib/securityNames';

/**
 * The actions an analysis can be run for — the backend's AnalysisActionType.
 *
 * The token alone decides which engine runs: rebalance and concentration go to
 * the deterministic rebalance engine, an STP with both ends known to the STP
 * builder, and everything else to that action's sub-agent.
 */
export const PROPOSAL_ACTIONS = [
  ['rebalance_portfolio', 'Rebalance the portfolio'],
  ['concentration_fix', 'Fix concentration'],
  ['risk_reduction', 'Reduce risk'],
  ['tax_efficiency', 'Tax efficiency'],
  ['cash_invest', 'Invest cash'],
  ['raise_cash', 'Raise cash'],
  ['exit_position', 'Exit a position'],
  ['swap_holding', 'Swap a holding'],
  ['exposure_replacement', 'Replace an exposure'],
  ['stp_swap', 'STP swap'],
  ['swp_setup', 'Set up an SWP'],
  ['analyse_security', 'Analyse a security'],
];
export const PROPOSAL_ACTION_LABEL = Object.fromEntries(PROPOSAL_ACTIONS);

/**
 * Which engine built a saved plan. The backend's only classification of a
 * proposal: there is no draft / active / executed state on the row.
 */
export const PROPOSAL_SOURCE_LABEL = {
  subagent: 'Agent plan',
  rebalance: 'Rebalance',
  stp: 'STP',
  resize: 'Resize',
};

/**
 * A client's saved proposals, kept current.
 *
 * Polled, because the list changes without this screen doing anything: an
 * agent can run an analysis that someone else saves, or a colleague saves one
 * from another session. Thirty seconds is often enough to see those arrive
 * without hammering a list that rarely changes.
 */
export function useInvestmentProposals(clientId, {
  goalId = null, limit = 50, pollMs = 30000, skip = false,
} = {}) {
  const off = skip || clientId == null || clientId === '';
  const { data, loading, error, refetch, networkStatus } = useQuery(LIST_INVESTMENT_PROPOSALS, {
    variables: { goalId: goalId == null ? null : Number(goalId), limit, offset: 0 },
    context: asClient(clientId),
    skip: off,
    fetchPolicy: 'cache-and-network',
    pollInterval: off ? 0 : pollMs,
    errorPolicy: 'all',
  });

  return useMemo(() => {
    const res = data?.listInvestmentProposals ?? null;
    return {
      loading: loading && !res,
      /* A background poll is not a reload: 6 is Apollo's "poll" status. */
      refreshing: networkStatus === 4 || networkStatus === 6,
      error,
      refetch,
      /* `success: false` with a message is the resolver refusing, which the
         screen should say — distinct from an empty list. */
      refused: res && res.success === false ? (res.message || 'The proposals service refused the request') : null,
      rows: res?.proposals ?? EMPTY,
    };
  }, [data, loading, error, refetch, networkStatus]);
}

/** One recommendation's plan, fetched only when a row is opened. */
export function useInvestmentRecommendation(urlId, clientId, { skip = false } = {}) {
  const { data, loading, error } = useQuery(INVESTMENT_RECOMMENDATION, {
    variables: { urlId },
    context: asClient(clientId),
    skip: skip || !urlId,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  return useMemo(() => {
    const r = data?.getInvestmentRecommendation ?? null;
    return {
      loading,
      error,
      /* "not_found_or_expired" is the resolver's own word for a run whose
         working copy is gone and was never saved. */
      missing: r && r.success === false ? (r.message || 'not found') : null,
      data: r?.data ?? null,
    };
  }, [data, loading, error]);
}

const isSellSide = (side) => /sell|redeem|exit|switch.?out|withdraw/i.test(String(side || ''));

/**
 * A saved proposal's plan, as the trades that would place it.
 *
 * ONE reading of a plan into legs, shared by every place that offers to trade
 * one — the proposal's own page and each row of the list. Two copies would be
 * two chances to disagree about what is about to be bought with a client's
 * money.
 *
 * The legs are the first plan's execution legs with skipped and unpriced ones
 * left out. A plan that buys a MODEL is refused rather than traded: a model
 * leg is bought through the model order flow, and placing it as a plain trade
 * would be wrong. `blocked` says why, in words a button's tooltip can carry.
 *
 * `skip` holds the read back until someone actually asks to trade — the list
 * mounts one of these per row and should not fetch every plan to draw a button.
 */
export function useProposalLegs(urlId, clientId, { skip = false } = {}) {
  const rec = useInvestmentRecommendation(urlId, clientId, { skip });
  const plan = rec.data?.plans?.[0] ?? null;
  const legs = useMemo(() => {
    /* The leg's own `fundName` is often null. The plan's candidates carry the
       name, so what is about to be traded reads as a fund rather than an ISIN
       on the one screen that places real orders. */
    const names = nameIndex(plan);
    return (plan?.executionPlan?.executionLegs ?? [])
      .filter((l) => !l.skipped && Number(l.tradeValue) > 0)
      .map((l) => ({
        securitySymbol: l.holdingSecuritySymbol,
        side: isSellSide(l.side) ? 'SELL' : 'BUY',
        amount: Number(l.tradeValue),
        allRedeem: false,
        name: nameFor(l.holdingSecuritySymbol, names, l.fundName),
      }));
  }, [plan]);
  const hasModelLeg = (plan?.candidates ?? []).some((c) => c.modelId != null);
  let blocked = null;
  if (rec.missing) blocked = `This proposal could not be read: ${rec.missing}`;
  else if (hasModelLeg) blocked = 'This plan buys a model portfolio, which is placed through the model order flow rather than as trades';
  else if (!rec.loading && rec.data && !legs.length) blocked = 'This plan has no priced legs to place';
  return { legs, blocked, loading: rec.loading, error: rec.error, rec };
}

/**
 * Read one recommendation on demand, with the same query and variables as
 * `useInvestmentRecommendation`, so a caller and the view share a cache entry.
 * Resolves the resolver's answer (`{ success, message, data }`) or null.
 */
export function useRecommendationReader(clientId) {
  const client = useApolloClient();
  return useCallback(async (urlId) => {
    const { data, errors } = await client.query({
      query: INVESTMENT_RECOMMENDATION,
      variables: { urlId },
      context: asClient(clientId),
      fetchPolicy: 'cache-first',
      errorPolicy: 'all',
    });
    if (errors?.length) throw new Error(errors.map((e) => e.message).join('; '));
    return data?.getInvestmentRecommendation ?? null;
  }, [client, clientId]);
}

const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

/* A request that never reached the server, in the wording each browser uses. */
const NETWORK_FAILURE = /Failed to fetch|NetworkError when attempting to fetch|Load failed|Network request failed/i;
/* How many times one poll is retried after a dropped connection. */
const POLL_RETRIES = 3;

/* A job's status is kept by the backend for 5 minutes; past that a poll only
   answers "unknown", so a remembered job older than this is not followed. */
const JOB_MEMORY_MS = 5 * 60 * 1000;
/* A finished analysis is held unsaved for 24 hours (the working copy). */
const RESULT_MEMORY_MS = 24 * 60 * 60 * 1000;
const IDLE = Object.freeze({ status: 'idle', jobId: null, urlId: null, message: null });

/**
 * Start an analysis and wait for its urlId.
 *
 * The run is asynchronous — measured at over a minute — so it is started, then
 * polled by sending the same query again with its jobId (Ki_UI's analysisJob)
 * until the job reports done or failed. Every call goes out uncached: the poll
 * sends identical variables each time and needs a fresh answer, which the cache
 * would never give it.
 *
 * `start` resolves to the final state, so a caller can await it; the hook's own
 * state drives the screen meanwhile. Unmounting stops the polling, not the job.
 *
 * With a `key`, the job is remembered in the `analysisJobs` slice: a later
 * mount with the same key follows a job still running to its end, and shows a
 * finished analysis again, instead of the screen forgetting both. `reset`
 * forgets it. Without a key nothing is remembered.
 */
export function useProposalRun(clientId, { key = null, intervalMs = 5000, maxPolls = 90 } = {}) {
  const client = useApolloClient();
  const dispatch = useDispatch();
  const store = useStore();
  const alive = useRef(true);
  const runToken = useRef(0);
  const [state, setState] = useState(IDLE);

  /* Set on every mount, not only the first: StrictMode unmounts and remounts
     once, and a flag left false would cancel every run started after that. */
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const ask = useCallback(async (variables) => {
    const { data, errors } = await client.query({
      query: RUN_INVESTMENT_ANALYSIS,
      variables,
      context: asClient(clientId),
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    });
    if (errors?.length) throw new Error(errors.map((e) => e.message).join('; '));
    return data?.runInvestmentAnalysis ?? null;
  }, [client, clientId]);

  /** Start `vars` — or, given `resumeJobId`, follow that job — until it settles. */
  const follow = useCallback(async (vars, resumeJobId = null) => {
    const token = (runToken.current += 1);
    const current = () => alive.current && token === runToken.current;
    const finish = (next) => {
      if (current()) setState(next);
      return next;
    };
    const forget = () => { if (key) dispatch(analysisJobCleared({ key })); };

    setState({ status: 'running', jobId: resumeJobId, urlId: null, message: null });
    let remembered = resumeJobId;
    try {
      let r = await ask(resumeJobId ? { ...vars, jobId: resumeJobId } : vars);
      for (let i = 0; ; i += 1) {
        if (!current()) return { status: 'cancelled' };
        if (!r) {
          forget();
          return finish({ status: 'failed', jobId: null, urlId: null, message: 'The analysis returned nothing' });
        }

        const status = String(r.jobStatus || '').toLowerCase();
        if (r.urlId && (status === 'done' || status === '')) {
          if (key) {
            dispatch(analysisJobFinished({
              key, urlId: r.urlId, jobId: r.jobId ?? remembered, variables: vars, at: Date.now(),
            }));
          }
          return finish({ status: 'done', jobId: r.jobId ?? null, urlId: r.urlId, message: r.message ?? null });
        }
        if (status === 'failed' || status === 'unknown' || (r.success === false && status !== 'running')) {
          forget();
          return finish({
            status: 'failed', jobId: r.jobId ?? null, urlId: null, message: r.message || 'The analysis did not finish',
          });
        }
        if (!r.jobId) {
          forget();
          return finish({ status: 'failed', jobId: null, urlId: null, message: r.message || 'No job was started' });
        }
        /* Remembered from the moment it exists: the mount that is left two
           seconds later is exactly the one whose job must still be findable. */
        if (key && r.jobId !== remembered) {
          dispatch(analysisJobStarted({
            key, jobId: r.jobId, variables: vars, at: Date.now(),
          }));
          remembered = r.jobId;
        }
        if (i >= maxPolls) {
          return finish({
            status: 'failed', jobId: r.jobId, urlId: null, message: 'Still running after several minutes — try opening it again later',
          });
        }
        if (current()) setState({ status: 'running', jobId: r.jobId, urlId: null, message: null });
        await sleep(intervalMs);
        if (!current()) return { status: 'cancelled' };
        /* A poll only reads the job, so a dropped connection is retried rather
           than abandoning a job the server is still running. The start above is
           never retried: it could start a second job. */
        const { jobId } = r;
        for (let attempt = 0; ; attempt += 1) {
          try {
            r = await ask({ ...vars, jobId });
            break;
          } catch (err) {
            if (attempt >= POLL_RETRIES || !NETWORK_FAILURE.test(err.message)) throw err;
            await sleep(intervalMs);
            if (!current()) return { status: 'cancelled' };
          }
        }
      }
    } catch (err) {
      /* A job that was started stays remembered: it may well still be running. */
      return finish({ status: 'failed', jobId: null, urlId: null, message: err.message });
    }
  }, [ask, dispatch, key, intervalMs, maxPolls]);

  const start = useCallback((inputs) => {
    /* A stored `runInputs` may carry the job id of the run it came from;
       re-sending it would only fetch that old job's status. */
    const { jobId: _stale, ...base } = inputs ?? {};
    return follow({ ...base, asyncMode: base.asyncMode ?? true });
  }, [follow]);

  /* What this key remembers, on mount and whenever the key changes. A loop
     following the previous key's job stops following it; that job, and what
     it remembers, stay. */
  useEffect(() => {
    runToken.current += 1;
    const job = selectAnalysisJob(store.getState(), key);
    const now = Date.now();
    if (job?.urlId && now - (job.finishedAt ?? 0) < RESULT_MEMORY_MS) {
      setState({ status: 'done', jobId: job.jobId, urlId: job.urlId, message: null });
    } else if (job?.jobId && !job.urlId && job.variables && now - (job.startedAt ?? 0) < JOB_MEMORY_MS) {
      follow(job.variables, job.jobId);
    } else {
      if (job) dispatch(analysisJobCleared({ key }));
      setState(IDLE);
    }
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  const reset = useCallback(() => {
    runToken.current += 1;
    if (key) dispatch(analysisJobCleared({ key }));
    setState(IDLE);
  }, [dispatch, key]);

  return { ...state, running: state.status === 'running', start, reset };
}
